import { Lead, Followup, Activity, DashboardStats } from '../types'

const API_BASE = '/api'

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'API request failed')
  }
  
  return response.json()
}

// Leads API
export const leadsApi = {
  list: (params?: { stage?: string; search?: string }) => {
    const query = new URLSearchParams(params).toString()
    return fetchApi<Lead[]>(`/leads${query ? `?${query}` : ''}`)
  },
  
  get: (id: number) => fetchApi<Lead>(`/leads/${id}`),
  
  create: (lead: Partial<Lead>) => fetchApi<Lead>('/leads', {
    method: 'POST',
    body: JSON.stringify(lead),
  }),
  
  update: (id: number, lead: Partial<Lead>) => fetchApi<Lead>(`/leads/${id}`, {
    method: 'PUT',
    body: JSON.stringify(lead),
  }),
  
  delete: (id: number) => fetchApi<void>(`/leads/${id}`, {
    method: 'DELETE',
  }),
  
  updateStage: (id: number, stage: string) => fetchApi<Lead>(`/leads/${id}/stage`, {
    method: 'PATCH',
    body: JSON.stringify({ stage }),
  }),
}

// Follow-ups API
export const followupsApi = {
  list: (params?: { date?: string; lead_id?: number }) => {
    const query = new URLSearchParams(params as any).toString()
    return fetchApi<Followup[]>(`/followups${query ? `?${query}` : ''}`)
  },
  
  today: () => fetchApi<Followup[]>('/followups/today'),
  
  create: (followup: Partial<Followup>) => fetchApi<Followup>('/followups', {
    method: 'POST',
    body: JSON.stringify(followup),
  }),
  
  update: (id: number, followup: Partial<Followup>) => fetchApi<Followup>(`/followups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(followup),
  }),
  
  complete: (id: number) => fetchApi<Followup>(`/followups/${id}/complete`, {
    method: 'PATCH',
  }),
  
  reschedule: (id: number, newDate: string) => fetchApi<Followup>(`/followups/${id}/reschedule`, {
    method: 'PATCH',
    body: JSON.stringify({ new_date: newDate }),
  }),
}

// Activities API
export const activitiesApi = {
  list: (leadId: number) => fetchApi<Activity[]>(`/activities?lead_id=${leadId}`),
  
  create: (activity: Partial<Activity>) => fetchApi<Activity>('/activities', {
    method: 'POST',
    body: JSON.stringify(activity),
  }),
}

// Dashboard API
export const dashboardApi = {
  stats: () => fetchApi<DashboardStats>('/dashboard/stats'),
  recentActivity: () => fetchApi<Activity[]>('/dashboard/recent-activity'),
}
